/* ==========================================================================
   AxisFold modular UI — behaviour for the statistics block and the sticky
   add-to-cart bar. Loaded after afx-core.js and hooks into the same AFX.init
   pass, so anything the theme editor re-renders is picked up automatically.
   ========================================================================== */

(function () {
  'use strict';

  var AFX = (window.AFX = window.AFX || {});

  function $$(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }
  function num(v, fallback) {
    var n = parseFloat(v);
    return isNaN(n) ? fallback : n;
  }
  function once(el, key) {
    if (el.getAttribute('data-afx-ready-x') === key) return false;
    el.setAttribute('data-afx-ready-x', key);
    return true;
  }
  function reduced() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ---------- statistics: rings, bars and counting numbers -------------- */

  var RING_LENGTH = 263.9;

  /* keeps whatever the merchant typed around the number: 4.8, 1,200, 10k */
  function countTo(el, targetText) {
    var match = String(targetText).match(/-?[\d.,]+/);
    if (!match) return;
    var rawNumber = match[0];
    var decimals = (rawNumber.split('.')[1] || '').length;
    var target = num(rawNumber.replace(/,/g, ''), 0);
    var grouped = rawNumber.indexOf(',') > -1;
    var before = targetText.slice(0, match.index);
    var after = targetText.slice(match.index + rawNumber.length);
    var start = null;
    var duration = 1100;

    function paint(value) {
      var text = value.toFixed(decimals);
      if (grouped) {
        var parts = text.split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        text = parts.join('.');
      }
      el.textContent = before + text + after;
    }

    function step(now) {
      if (start === null) start = now;
      var p = Math.min(1, (now - start) / duration);
      /* ease out so the last digits settle rather than snap */
      paint(target * (1 - Math.pow(1 - p, 3)));
      if (p < 1) requestAnimationFrame(step);
      else paint(target);
    }

    paint(0);
    requestAnimationFrame(step);
  }

  function fillStat(el) {
    $$('[data-afx-ring]', el).forEach(function (arc) {
      var pct = Math.max(0, Math.min(100, num(arc.getAttribute('data-afx-ring'), 0)));
      arc.style.strokeDashoffset = RING_LENGTH - (RING_LENGTH * pct) / 100;
    });
    $$('[data-afx-bar]', el).forEach(function (bar) {
      bar.style.width = Math.max(0, Math.min(100, num(bar.getAttribute('data-afx-bar'), 0))) + '%';
    });
    $$('[data-afx-count]', el).forEach(function (node) {
      if (reduced()) return;
      countTo(node, node.getAttribute('data-afx-count') || node.textContent);
    });
  }

  function initStat(el) {
    if (!once(el, 'stat')) return;
    if (!('IntersectionObserver' in window)) {
      fillStat(el);
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          fillStat(entry.target);
          io.unobserve(entry.target);
        });
      },
      { threshold: 0.3, rootMargin: '0px 0px -5% 0px' }
    );
    io.observe(el);
  }

  /* ---------- sticky add to cart bar ------------------------------------ */

  /* The bar usually sits on a page that already has a real add-to-cart block.
     Rather than asking the merchant to paste a sync group, the sticky button
     mirrors that block's label and forwards its clicks, so bundle choices,
     option choices and the sold-out state follow along on their own. */
  function initMirror(bar) {
    var own = bar.querySelector('[data-afx-atc]');
    if (!own) return;

    var others = $$('[data-afx-atc]').filter(function (node) {
      return !node.closest('[data-afx-sticky]');
    });
    var main = others[0];
    if (!main) return;

    /* the merchant pasted a matching sync group — the scopes already share
       state, so leave the button to the normal engine */
    if (main.getAttribute('data-scope') === own.getAttribute('data-scope')) return;

    var target = main.querySelector('[data-afx-atc-btn]');
    var button = own.querySelector('[data-afx-atc-btn]');
    if (!target || !button) return;

    /* swapping in a clone drops the listener the engine already attached,
       so one click never adds to the cart twice */
    var proxy = button.cloneNode(true);
    proxy.removeAttribute('data-afx-atc-btn');
    button.parentNode.replaceChild(proxy, button);
    own.removeAttribute('data-afx-atc');

    function sync() {
      proxy.textContent = target.textContent;
      proxy.disabled = target.disabled;
      proxy.classList.toggle('is-done', target.classList.contains('is-done'));
    }

    sync();
    if ('MutationObserver' in window) {
      new MutationObserver(sync).observe(target, {
        childList: true,
        characterData: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['disabled', 'class']
      });
    }

    proxy.addEventListener('click', function () {
      target.click();
    });
  }

  function initStickyBar(el) {
    if (!once(el, 'stickybar')) return;

    initMirror(el);

    var after = num(el.getAttribute('data-show-after'), 500);
    var watchId = (el.getAttribute('data-watch') || '').replace(/^#/, '');
    var hideId = (el.getAttribute('data-hide-near') || '').replace(/^#/, '');
    var forced = false;
    var ticking = false;

    function onScreen(node) {
      var r = node.getBoundingClientRect();
      return r.bottom > 0 && r.top < (window.innerHeight || 0);
    }

    function decide() {
      ticking = false;
      if (forced) {
        el.classList.add('is-visible');
        return;
      }
      var show;
      var watch = watchId ? document.getElementById(watchId) : null;
      if (watch) {
        show = watch.getBoundingClientRect().bottom < 0;
      } else {
        show = (window.pageYOffset || document.documentElement.scrollTop) > after;
      }
      if (show && hideId) {
        var hideEl = document.getElementById(hideId);
        if (hideEl && onScreen(hideEl)) show = false;
      }
      el.classList.toggle('is-visible', show);
    }

    function request() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(decide);
    }

    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', request);
    decide();

    /* in the theme editor, show the bar while the merchant is editing it */
    var section = el.closest('.shopify-section') || el.parentElement;
    document.addEventListener('shopify:section:select', function (e) {
      if (e.target === section) {
        forced = true;
        decide();
      }
    });
    document.addEventListener('shopify:section:deselect', function (e) {
      if (e.target === section) {
        forced = false;
        decide();
      }
    });
  }

  /* ---------- boot ------------------------------------------------------ */

  function initExtras(root) {
    var ctx = root || document;
    $$('.afx-stat', ctx).forEach(initStat);
    $$('[data-afx-sticky]', ctx).forEach(initStickyBar);
  }

  var previous = AFX.init;
  AFX.init = function (root) {
    if (typeof previous === 'function') previous(root);
    initExtras(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      AFX.init();
    });
  } else {
    AFX.init();
  }

  ['shopify:section:load', 'shopify:section:reorder', 'shopify:block:select'].forEach(function (evt) {
    document.addEventListener(evt, function (e) {
      initExtras(e.target && e.target.nodeType === 1 ? e.target : document);
    });
  });
})();
